import { ExternalLlm, Llm } from '@kindo/universal';

import DisplayedSettingsModel, { CostTier } from './ModelChoice.types';

export const COST_TIER_INDICATOR_TEXT: Record<CostTier, string> = {
  [CostTier.LOW]: '$',
  [CostTier.MEDIUM]: '$$',
  [CostTier.HIGH]: '$$$'
};

/*
costTier
  ranked according to model's
    (input_cost_per_token + output_cost_per_token) / 2
  (values found in litellm/model_prices_and_context_window.json)
  LOW:    deepSeek = 2.1e-07, mixtral = 2.7e-07, llama = 7.2e-07, haiku = 7.5e-07
  MEDIUM: geminiFlash = 1e-06, gpt35Turbo = 1.75e-06, sonnet = 9e-06
  HIGH:   geminiPro = 1e-05, gpt-4o = 1e-05, gpt4Turbo = 2e-05, opus = 4.4999999999999996e-05, 
          gpt4 = 4.5e-05
*/
export const MODEL_COST_TIERS: Record<ExternalLlm, CostTier> = {
  // "input_cost_per_token": 0.000015, "output_cost_per_token": 0.000075,
  [Llm.CLAUDE_3_OPUS]: CostTier.HIGH,
  // "input_cost_per_token": 0.000001, "output_cost_per_token": 0.000005,
  [Llm.CLAUDE_3_5_HAIKU]: CostTier.MEDIUM,
  // "input_cost_per_token": 0.000003, "output_cost_per_token": 0.000015,
  [Llm.CLAUDE_3_5_SONNET]: CostTier.MEDIUM,
  // "input_cost_per_token": 0.000003, "output_cost_per_token": 0.000015,
  [Llm.CLAUDE_3_7_SONNET]: CostTier.MEDIUM,
  // "input_cost_per_token": 0.000007, "output_cost_per_token": 0.000007,
  [Llm.DEEPSEEK_R1]: CostTier.MEDIUM,
  // "input_cost_per_token": 0.00000015, "output_cost_per_token": 0.0000006
  [Llm.GEMINI_2_0_FLASH]: CostTier.LOW,
  // "input_cost_per_token": 0.000000075, "output_cost_per_token": 0.0000003
  [Llm.GEMINI_2_0_FLASH_LITE]: CostTier.LOW,
  // "input_cost_per_token": 0.00000125, "output_cost_per_token": 0.00001
  [Llm.GEMINI_2_5_PRO]: CostTier.HIGH,
  // "input_cost_per_token": 0.000005, "output_cost_per_token": 0.000015,
  [Llm.GPT_4O]: CostTier.HIGH,
  // "input_cost_per_token": 2e-6, "output_cost_per_token": 8e-6
  [Llm.GPT_4_1]: CostTier.HIGH,
  // "input_cost_per_token": 0.4e-6, "output_cost_per_token": 1.6e-6
  [Llm.GPT_4_1_MINI]: CostTier.LOW,
  // "input_cost_per_token": 0.0000006, "output_cost_per_token": 0.0000006
  [Llm.LLAMA_3_1_8B_INSTANT]: CostTier.LOW,
  [Llm.LLAMA_3_3_70B]: CostTier.LOW,
  // "input_cost_per_token": 0.000015, "output_cost_per_token": 0.00006
  [Llm.O1]: CostTier.HIGH,
  // "input_cost_per_token": 1e-5, "output_cost_per_token": 4e-5
  [Llm.O3]: CostTier.HIGH,
  // "input_cost_per_token": 1.1e-6, "output_cost_per_token": 4.4e-6
  [Llm.O4_MINI]: CostTier.LOW,
  // "input_cost_per_token": 0.0000011, "output_cost_per_token": 0.0000044
  [Llm.QWEN2_72B_INSTRUCT]: CostTier.LOW,
  // "input_cost_per_token": 0.0000012, "output_cost_per_token": 0.0000012
  [Llm.QWEN2_5_72B_INSTRUCT_TURBO]: CostTier.MEDIUM,
  [Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]: CostTier.LOW,
  [Llm.WHITERABBITNEO_33B]: CostTier.LOW,
  [Llm.WHITERABBITNEO_V3_7B]: CostTier.LOW
};

const MODELS: Record<ExternalLlm, DisplayedSettingsModel> = {
  [Llm.CLAUDE_3_5_SONNET]: {
    type: 'Text Generation',
    description:
      'Intelligent model in the Claude family, with the highest level of intelligence and capability for complex tasks such as context-sensitive customer support and orchestrating multi-step workflows.',
    usageTag: 'Chat + Agents',
    link: 'https://www.anthropic.com/news/claude-3-5-sonnet'
  },
  [Llm.CLAUDE_3_7_SONNET]: {
    type: 'Text Generation',
    description:
      'The most intelligent model in the Claude family to date and the first hybrid reasoning model on the market',
    usageTag: 'Chat + Agents',
    link: 'https://www.anthropic.com/news/claude-3-7-sonnet'
  },
  [Llm.CLAUDE_3_OPUS]: {
    type: 'Text Generation',
    description:
      'Opus outperforms its peers on most of the common evaluation benchmarks for AI systems, including undergraduate level expert knowledge (MMLU), graduate level expert reasoning (GPQA), basic mathematics (GSM8K), and more. It exhibits near-human levels of comprehension and fluency on complex tasks.',
    usageTag: 'Chat + Agents',
    link: 'https://www.anthropic.com/news/claude-3-family'
  },
  [Llm.CLAUDE_3_5_HAIKU]: {
    type: 'Text Generation',
    description:
      'Fast model optimized for quick responses while maintaining high quality output',
    usageTag: 'Chat + Agents',
    link: 'https://www.anthropic.com/news/3-5-models-and-computer-use'
  },
  [Llm.DEEPSEEK_R1]: {
    type: 'Text Generation',
    description:
      'A powerful reasoning model by DeepSeek that is highly competitive with other leading models',
    usageTag: 'Chat + Agents',
    link: 'https://api-docs.deepseek.com/news/news250120'
  },
  [Llm.GEMINI_2_0_FLASH]: {
    type: 'Multi-Modal',
    description:
      "A lightweight member of Google's Gemini family, competitive with Gemini 1.5 Pro.",
    usageTag: 'Chat + Agents',
    link: 'https://deepmind.google/technologies/gemini/flash/'
  },
  [Llm.GEMINI_2_0_FLASH_LITE]: {
    type: 'Multi-Modal',
    description:
      'A Gemini 2.0 Flash model optimized for cost efficiency and low latency.',
    usageTag: 'Chat + Agents',
    link: 'https://deepmind.google/technologies/gemini/flash/'
  },
  [Llm.GEMINI_2_5_PRO]: {
    type: 'Multi-Modal',
    description: [
      "Google's state-of-the-art thinking model, capable of reasoning over complex problems in code, math, and STEM,",
      'as well as analyzing large datasets, codebases, and documents using long context.',
      'Gemini 2.5 Pro rate limits are more restricted since it is an experimental/preview model.'
    ].join(' '),
    usageTag: 'Chat + Agents',
    link: 'https://deepmind.google/technologies/gemini/pro/'
  },
  [Llm.GPT_4O]: {
    type: 'Text Generation',
    description:
      'This model is known for its high-speed, advanced text generation capabilities.',
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/gpt-4o'
  },
  [Llm.GPT_4_1]: {
    type: 'Text Generation',
    description:
      "OpenAI's flagship model for complex tasks. It is well suited for problem solving across domains.",
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/gpt-4.1'
  },
  [Llm.GPT_4_1_MINI]: {
    type: 'Text Generation',
    description:
      'GPT-4.1 mini provides a balance between intelligence, speed, and cost that makes it an attractive model for many use cases.',
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/gpt-4.1-mini'
  },
  [Llm.LLAMA_3_1_8B_INSTANT]: {
    type: 'Text Generation',
    description:
      'Llama 3.1 8B running on Groq infrastructure for faster inference.',
    usageTag: 'Chat + Agents',
    link: 'https://llama.meta.com/llama3'
  },
  [Llm.LLAMA_3_3_70B]: {
    type: 'Text Generation',
    description: 'Llama 3.3 70B is an open source model developed by Meta AI.',
    usageTag: 'Chat + Agents',
    link: 'https://llama.meta.com/docs/model-cards-and-prompt-formats/llama3_3'
  },
  [Llm.O1]: {
    type: 'Text Generation',
    description: "OpenAI's model for advanced reasoning capabilities.",
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/o1-mini#o1'
  },
  [Llm.O3]: {
    type: 'Text Generation',
    description: [
      'o3 is a well-rounded and powerful model across domains. It sets a new standard for math, science, coding, and,',
      'visual reasoning tasks. It also excels at technical writing and instruction-following. Use it to think through',
      'multi-step problems that involve analysis across text, code, and images'
    ].join(' '),
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/o3'
  },
  [Llm.O4_MINI]: {
    type: 'Text Generation',
    description:
      "OpenAI's latest small o-series model. It's optimized for fast, effective reasoning with exceptionally efficient performance in coding and visual tasks.",
    usageTag: 'Chat + Agents',
    link: 'https://platform.openai.com/docs/models/o4-mini#o4-mini'
  },
  [Llm.QWEN2_72B_INSTRUCT]: {
    type: 'Text Generation',
    description:
      'This model is known to excel in language understanding, multilingual capabilities, coding, mathematics, and reasoning.',
    usageTag: 'Chat + Agents',
    link: 'https://arxiv.org/html/2407.10671v1'
  },
  [Llm.QWEN2_5_72B_INSTRUCT_TURBO]: {
    type: 'Text Generation',
    description: 'This model is an upgraded version of Qwen 2.',
    usageTag: 'Chat + Agents',
    link: 'https://arxiv.org/abs/2412.15115'
  },
  [Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B]: {
    type: 'Text Generation',
    description: 'Known for its advanced cybersecurity capabilities.',
    usageTag: 'Chat + Agents',
    link: 'https://huggingface.co/WhiteRabbitNeo'
  },
  [Llm.WHITERABBITNEO_33B]: {
    type: 'Text Generation + Code Completion',
    description: 'Known for its proficiency in coding and cybersecurity.',
    usageTag: 'Chat + Agents',
    link: 'https://huggingface.co/WhiteRabbitNeo/WhiteRabbitNeo-33B-v1.5'
  },
  [Llm.WHITERABBITNEO_V3_7B]: {
    type: 'Text Generation',
    description:
      'A lightweight and powerful version of WhiteRabbitNeo that is highly proficient in coding and cybersecurity.',
    usageTag: 'Chat + Agents',
    link: 'https://huggingface.co/WhiteRabbitNeo/WhiteRabbitNeo-V3-7B'
  }
};

export default MODELS;
